// src/three/loaders/createPLYMesh.js
import * as THREE from "three";
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader.js";

export async function createPLYMesh({
  plyUrl,
  textureUrl,
  setProgress, // ← 여기에 넘겨받은 setter 사용
  doubleSide = true,
  castShadows = true,
  rotateZPI = true,
  normalizeToUnit = true,
  materialOptions = {},
} = {}) {
  if (!plyUrl) throw new Error("createPLYMesh: plyUrl is required");

  // 내부 누적 진행률 상태
  const weightTex = textureUrl ? 0.3 : 0; // 텍스처 30%
  const weightPly = 0.7 + (textureUrl ? 0 : 0.3); // PLY 70% (+텍스처 없으면 100%)
  let texProgress = textureUrl ? 0 : 1;
  let plyProgress = 0;

  const report = () => {
    if (!setProgress) return;
    const p = Math.min(
      100,
      Math.round((texProgress * weightTex + plyProgress * weightPly) * 100)
    );
    setProgress(p);
  };

  setProgress?.(0);

  // 1) Texture (비동기 로드 + 진행률)
  let texture = undefined;
  if (textureUrl) {
    const tl = new THREE.TextureLoader();
    texture = await new Promise((resolve, reject) => {
      tl.load(
        textureUrl,
        (tex) => {
          // 최신 three: sRGB 권장
          if ("colorSpace" in tex) tex.colorSpace = THREE.SRGBColorSpace;
          tex.flipY = true;
          texProgress = 1;
          report();
          resolve(tex);
        },
        // onProgress: 서버가 Content-Length를 제공해야 동작
        (ev) => {
          if (ev?.lengthComputable && ev.total > 0) {
            texProgress = ev.loaded / ev.total;
          } else {
            // 총 길이를 모르면 느리게 증가 (발진 방지)
            texProgress = Math.min(0.95, texProgress + 0.05);
          }
          report();
        },
        (err) => reject(err)
      );
    });
  }

  // 2) PLY (Promise + 진행률)
  const geometry = await new Promise((resolve, reject) => {
    new PLYLoader().load(
      plyUrl,
      (geo) => {
        plyProgress = 1;
        report();
        resolve(geo);
      },
      (ev) => {
        if (ev?.lengthComputable && ev.total > 0) {
          plyProgress = ev.loaded / ev.total;
        } else {
          plyProgress = Math.min(0.98, plyProgress + 0.02);
        }
        report();
      },
      (err) => reject(err)
    );
  });

  // 3) 지오메트리 정리
  geometry.computeVertexNormals();
  geometry.center();

  // 4) 머티리얼
  const material = new THREE.MeshStandardMaterial({
    map: texture,
    side: doubleSide ? THREE.DoubleSide : THREE.FrontSide,
    roughness: 1.0,
    metalness: 0,
    vertexColors: false,
    ...materialOptions,
  });

  // 5) 메시 생성
  const mesh = new THREE.Mesh(geometry, material);

  // 6) 최대 치수 기준 정규화
  if (normalizeToUnit) {
    const box = new THREE.Box3().setFromObject(mesh);
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    const scale = 1.0 / maxDim;
    mesh.scale.setScalar(scale);
  }

  // 7) 회전/그림자
  if (rotateZPI) mesh.rotateZ(Math.PI);
  mesh.castShadow = !!castShadows;
  mesh.receiveShadow = true;

  // 마지막 100%
  setProgress?.(100);

  return { mesh, geometry, material, texture };
}
